import * as React from 'react'
import { addDomEvent } from 'utils/dom'

// -- Constants
export const BREAKPOINTS = {
  SMALL: 640,
  MEDIUM: 768,
  LARGE: 1024,
  EXTRA_LARGE: 1280,
  '2_EXTRA_LARGE': 1536,
  '3_EXTRA_LARGE': 1920,
}

export const CONTAINER_BREAKPOINTS = {
  '3xl': 768,
  '5xl': 1024,
}

/** Narrowest first. Every breakpoint comparison derives from this order. */
export const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const

export type Breakpoints = (typeof BREAKPOINT_ORDER)[number]

/** True when `breakpoint` is narrower than `boundary`, which is itself never included. */
export function isBreakpointBelow(breakpoint: Breakpoints, boundary: Breakpoints) {
  return BREAKPOINT_ORDER.indexOf(breakpoint) < BREAKPOINT_ORDER.indexOf(boundary)
}

// Server has no window — return stable defaults that assume a desktop viewport.
// This avoids hydration mismatches for the most common user agent and keeps
// SSR-rendered layouts consistent with what desktop users see on first paint.
const getServerBreakpointSnapshot = (): Breakpoints => 'xl'
const getServerIsCompactSnapshot = () => false
const getServerIsSmallSnapshot = () => false

export function useIsCompactViewport() {
  return React.useSyncExternalStore(
    subscribe,
    getIsCompactViewportSnapshot,
    getServerIsCompactSnapshot,
  )
}

export function useIsSmallViewport() {
  return React.useSyncExternalStore(subscribe, getIsSmallViewportSnapshot, getServerIsSmallSnapshot)
}

export function useBreakpoint(): Breakpoints {
  return React.useSyncExternalStore(subscribe, getBreakpointSnapshot, getServerBreakpointSnapshot)
}

const subscribe = (callback: () => void) => {
  return addDomEvent(window, 'resize', callback)
}

export function getBreakpointSnapshot(): Breakpoints {
  const width = window.innerWidth
  return getBreakpoint(width)
}

export function getBreakpoint(width: number): Breakpoints {
  if (width < BREAKPOINTS.SMALL) {
    return 'xs'
  }
  if (width < BREAKPOINTS.MEDIUM) {
    return 'sm'
  }
  if (width < BREAKPOINTS.LARGE) {
    return 'md'
  }
  if (width < BREAKPOINTS.EXTRA_LARGE) {
    return 'lg'
  }
  if (width < BREAKPOINTS['2_EXTRA_LARGE']) {
    return 'xl'
  }
  if (width < BREAKPOINTS['3_EXTRA_LARGE']) {
    return '2xl'
  }
  return '3xl'
}

function getIsCompactViewportSnapshot(): boolean {
  return isBreakpointBelow(getBreakpointSnapshot(), 'lg')
}

function getIsSmallViewportSnapshot(): boolean {
  return isBreakpointBelow(getBreakpointSnapshot(), 'md')
}
